import { QUERY_KEYS } from '@/constants/reactQueryKeys'
import { useQueryWithHandler } from '@/hooks/useQueryWithHandler'
import { AssetDetail, DraftAsset } from '@/modules/Asset/types'
import { useCallback, useEffect, useMemo, useState } from 'react'
import toast from 'react-hot-toast'
import { useTranslation } from 'react-i18next'
import {
    AssetSubmitPayload,
    CreateAssetDraftPayload,
    CreateAssetVersionPayload,
    getAssetDetail,
    getDraftAssetDetail,
    UploadedFile,
} from '../../../services'
import {
    computeNextVersion,
    computeVersionBump,
    VersionBumpType,
} from '../../../utils/version-bump'
import { useAssetMutations } from './use-asset-mutations'

export interface AssetFormData {
    // Step 1: Basic Information
    assetName: string
    assetDescription: string
    documentGroup: string
    documentType: string
    confidentialityLevel: string
    integrityLevel: string
    availabilityLevel: string
    departmentId: string
    // Step 1: Sharing
    shareScope: 'public' | 'private'
    shared_employee_ids: number[]
    shared_department_ids: number[]
    // Step 1: Effective Dates
    startDate: string
    endDate: string
    // Step 2: Files
    files: File[]
    uploadedFiles?: UploadedFile[]
    // Additional
    notes: string
    isReadConfirmationRequired: boolean
    isDownloadRestricted: boolean
    // Notification recipients on publish
    notifyScope: 'none' | 'all' | 'custom'
    notify_employee_ids: number[]
    notify_department_ids: number[]
}

/** Giá trị khởi tạo mặc định của form tạo tài liệu (dùng cho useState + so sánh draft). */
export const buildInitialAssetFormData = (): AssetFormData => ({
    assetName: '',
    assetDescription: '',
    documentGroup: '',
    documentType: '',
    confidentialityLevel: '',
    integrityLevel: '',
    availabilityLevel: '',
    departmentId: '',
    shareScope: 'public',
    shared_employee_ids: [],
    shared_department_ids: [],
    startDate: new Date().toISOString().split('T')[0],
    endDate: '',
    files: [],
    notes: '',
    isReadConfirmationRequired: false,
    isDownloadRestricted: false,
    notifyScope: 'none',
    notify_employee_ids: [],
    notify_department_ids: [],
})

interface UseCreateAssetFormProps {
    id?: string
    /** When true, fetches published asset by slug for version update */
    isUpdateMode?: boolean
    onDraftSaved?: (draft: DraftAsset | AssetDetail) => void
    onAssetSubmitted?: () => void
}

export const useCreateAssetForm = ({
    id,
    isUpdateMode = false,
    onDraftSaved,
    onAssetSubmitted,
}: UseCreateAssetFormProps) => {
    const { t } = useTranslation('asset')

    // Hydration gate: ensure formData is prepared before rendering step content
    const [isFormReady, setIsFormReady] = useState(!id)
    const [draftAsset, setDraftAsset] = useState<
        DraftAsset | AssetDetail | null
    >(null)

    // Track original form data for version bump computation (update mode only)
    const [originalFormData, setOriginalFormData] =
        useState<AssetFormData | null>(null)
    const [originalFileCount, setOriginalFileCount] = useState(0)
    const [currentVersion, setCurrentVersion] = useState({
        major: 0,
        minor: 0,
        patch: 0,
    })

    const [formData, setFormData] = useState<AssetFormData>(
        buildInitialAssetFormData
    )

    // Helper to extract IDs safely
    const getAssetIds = useCallback(() => {
        if (!draftAsset) return { assetId: '', versionId: '' }

        if ('asset_id' in draftAsset) {
            const detail = draftAsset as AssetDetail
            return {
                assetId: detail.asset_id,
                versionId: detail.current_version?.asset_version_id || '',
            }
        }

        const nested = draftAsset as DraftAsset
        return {
            assetId: nested.asset?.asset_id,
            versionId: nested.asset_version?.asset_version_id,
        }
    }, [draftAsset])

    // Mutations (extracted to separate hook)
    const {
        createDraftMutation,
        updateDraftMutation,
        submitAssetMutation,
        createVersionMutation,
        isSaving,
        isSubmitting,
    } = useAssetMutations({
        getAssetIds,
        setDraftAsset: (draft) => setDraftAsset(draft),
        onDraftSaved,
        onAssetSubmitted,
    })

    // Fetch asset detail
    const { data: fetchedDraft } = useQueryWithHandler({
        queryKey: [QUERY_KEYS.ASSET_DETAIL, 'form', id],
        queryFn: () =>
            isUpdateMode ? getAssetDetail(id!) : getDraftAssetDetail(id!),
        enabled: !!id,
    })

    // Hydrate form data from fetched draft/asset
    useEffect(() => {
        if (!id) {
            setIsFormReady(true)
            return
        }

        if (!fetchedDraft) return

        const nextDraftAsset = fetchedDraft?.result
        if (!nextDraftAsset) {
            setIsFormReady(true)
            return
        }

        setDraftAsset(nextDraftAsset)

        const mappedData = mapDraftToFormData(nextDraftAsset, isUpdateMode)

        // Pre-populate uploaded files for update mode
        if (
            isUpdateMode &&
            'asset_id' in nextDraftAsset &&
            (nextDraftAsset as AssetDetail).files
        ) {
            const assetFiles = (nextDraftAsset as AssetDetail).files
            const existingUploadedFiles: UploadedFile[] = assetFiles.map(
                (f) => ({
                    file_name: f.file_name || '',
                    // Existing files are re-submitted by id (existing_file_ids), never by path,
                    // so the API no longer returns the S3 key and none is needed here.
                    file_path: '',
                    file_size: f.file_size || 0,
                    file_type: f.mime_type,
                    uploaded_at: f.created_at,
                    asset_file_id: f.asset_file_id,
                })
            )
            mappedData.uploadedFiles = existingUploadedFiles
            setOriginalFileCount(existingUploadedFiles.length)
        }

        // Store version info for update mode
        if (isUpdateMode && 'asset_id' in nextDraftAsset) {
            const detail = nextDraftAsset as AssetDetail
            if (detail.current_version) {
                setCurrentVersion({
                    major: detail.current_version.major,
                    minor: detail.current_version.minor,
                    patch: detail.current_version.patch,
                })
            }
        }

        setFormData((prev) => ({ ...prev, ...mappedData }))

        // Snapshot original data for version bump detection
        if (isUpdateMode) {
            setOriginalFormData((prev) =>
                prev ? prev : ({ ...mappedData } as AssetFormData)
            )
        }

        setIsFormReady(true)
    }, [id, fetchedDraft, isUpdateMode])

    // Version bump computation (update mode only)
    const filesChanged = useMemo(() => {
        if (!isUpdateMode || !originalFormData) return false
        const currentFiles = formData.uploadedFiles || []
        // Count changed (file added or removed)
        if (currentFiles.length !== originalFileCount) return true
        // Any newly uploaded file (no asset_file_id means user just uploaded it)
        if (currentFiles.some((f) => !f.asset_file_id)) return true
        // Same count + all have IDs: detect if ID sets differ (swap/reselect)
        const originalFiles = originalFormData.uploadedFiles || []
        const currentIds = new Set(currentFiles.map((f) => f.asset_file_id))
        return originalFiles.some((f) => !currentIds.has(f.asset_file_id))
    }, [
        isUpdateMode,
        originalFormData,
        formData.uploadedFiles,
        originalFileCount,
    ])

    const versionBumpType: VersionBumpType = useMemo(() => {
        if (!isUpdateMode || !originalFormData) return 'none'
        return computeVersionBump(originalFormData, formData, filesChanged)
    }, [isUpdateMode, originalFormData, formData, filesChanged])

    const nextVersion = useMemo(
        () => computeNextVersion(currentVersion, versionBumpType),
        [currentVersion, versionBumpType]
    )

    // Validation
    const areRequiredFieldsFilled = (): boolean =>
        !!(
            formData.assetName &&
            formData.documentGroup &&
            formData.documentType &&
            formData.confidentialityLevel &&
            formData.integrityLevel &&
            formData.availabilityLevel &&
            formData.departmentId
        )

    const validateStep1 = (): boolean => {
        if (!areRequiredFieldsFilled()) {
            toast.error(t('form.completeRequiredFields'))
            return false
        }

        if (formData.startDate && formData.endDate) {
            const start = new Date(formData.startDate)
            const end = new Date(formData.endDate)
            if (end < start) {
                toast.error(t('form.invalidDateRange'))
                return false
            }
        }

        if (
            formData.notifyScope === 'custom' &&
            formData.notify_employee_ids.length === 0 &&
            formData.notify_department_ids.length === 0
        ) {
            toast.error(t('form.notifyCustomRequired'))
            return false
        }

        return true
    }

    const validateStep2 = (): boolean => {
        return (formData.uploadedFiles?.length || 0) > 0
    }

    const saveDraft = () => {
        if (!validateStep1()) return false

        const draftPayload: CreateAssetDraftPayload = {
            asset_name: formData.assetName,
            asset_description: formData.assetDescription,
            document_group: formData.documentGroup,
            document_type: formData.documentType,
            confidentiality_level: formData.confidentialityLevel,
            integrity_level: formData.integrityLevel,
            availability_level: formData.availabilityLevel,
            department_id: Number(formData.departmentId),
            shared_employee_ids: formData.shared_employee_ids,
            shared_department_ids: formData.shared_department_ids,
            start_date: formData.startDate,
            end_date: formData.endDate || undefined,
            is_read_confirmation_required: formData.isReadConfirmationRequired,
            is_download_restricted: formData.isDownloadRestricted,
            notes: formData.notes,
            notify_scope: formData.notifyScope,
            notify_department_ids: formData.notify_department_ids,
            notify_employee_ids: formData.notify_employee_ids,
        }

        if (draftAsset) {
            updateDraftMutation.mutate(draftPayload)
        } else {
            createDraftMutation.mutate(draftPayload)
        }
        return true
    }

    const submitForm = () => {
        if (!validateStep1()) return

        const { assetId, versionId } = getAssetIds()

        if (isUpdateMode && versionBumpType !== 'none') {
            const existingFileIds = (formData.uploadedFiles || [])
                .filter((f) => !!f.asset_file_id)
                .map((f) => f.asset_file_id!)
            const newFilePaths = (formData.uploadedFiles || [])
                .filter((f) => !f.asset_file_id)
                .map((f) => f.file_path)

            const versionPayload: CreateAssetVersionPayload = {
                asset_name: formData.assetName,
                asset_description: formData.assetDescription,
                document_group: formData.documentGroup,
                document_type: formData.documentType,
                department_id: Number(formData.departmentId),
                confidentiality_level: formData.confidentialityLevel,
                integrity_level: formData.integrityLevel,
                availability_level: formData.availabilityLevel,
                start_date: formData.startDate || null,
                end_date: formData.endDate || null,
                is_read_confirmation_required:
                    formData.isReadConfirmationRequired,
                is_download_restricted: formData.isDownloadRestricted,
                notes: formData.notes,
                version_bump_type: versionBumpType,
                file_paths: newFilePaths.length > 0 ? newFilePaths : undefined,
                existing_file_ids:
                    existingFileIds.length > 0 ? existingFileIds : undefined,
                shared_employee_ids: formData.shared_employee_ids,
                shared_department_ids: formData.shared_department_ids,
                notify_scope: formData.notifyScope,
                notify_department_ids: formData.notify_department_ids,
                notify_employee_ids: formData.notify_employee_ids,
            }

            createVersionMutation.mutate({ assetId, data: versionPayload })
            return
        }

        // Create flow: submit via draft→submit pipeline
        if (!draftAsset) {
            toast.error(t('form.noDraftFound'))
            return
        }

        const filePaths =
            formData.uploadedFiles?.map((file) => file.file_path) || []

        const submissionPayload: AssetSubmitPayload = {
            asset_id: assetId,
            asset_version_id: versionId,
            file_paths: filePaths,
            asset_name: formData.assetName,
            asset_description: formData.assetDescription,
            document_group: formData.documentGroup,
            document_type: formData.documentType,
            confidentiality_level: formData.confidentialityLevel,
            integrity_level: formData.integrityLevel,
            availability_level: formData.availabilityLevel,
            department_id: Number(formData.departmentId),
            shared_employee_ids: formData.shared_employee_ids,
            shared_department_ids: formData.shared_department_ids,
            start_date: formData.startDate,
            end_date: formData.endDate || undefined,
            is_read_confirmation_required: formData.isReadConfirmationRequired,
            is_download_restricted: formData.isDownloadRestricted,
            notes: formData.notes,
            notify_scope: formData.notifyScope,
            notify_department_ids: formData.notify_department_ids,
            notify_employee_ids: formData.notify_employee_ids,
        }

        submitAssetMutation.mutate(submissionPayload)
    }

    const revertChanges = () => {
        if (!originalFormData) return
        setFormData((prev) => ({ ...prev, ...originalFormData }))
    }

    return {
        formData,
        setFormData,
        draftAsset,
        isFormReady,
        validateStep1,
        validateStep2,
        saveDraft,
        submitForm,
        revertChanges,
        isSaving,
        isSubmitting,
        versionBumpType,
        currentVersion,
        nextVersion,
        originalFormData,
        originalFileCount,
    }
}

/** Maps a fetched DraftAsset or AssetDetail into partial AssetFormData */
function mapDraftToFormData(
    nextDraftAsset: DraftAsset | AssetDetail,
    _isUpdateMode: boolean
): Partial<AssetFormData> {
    let assetName = ''
    let assetDescription = ''
    let documentGroup = ''
    let documentType = ''
    let confidentialityLevel = ''
    let integrityLevel = ''
    let availabilityLevel = ''
    let departmentId = ''
    let startDate = ''
    let endDate = ''
    let isReadConfirmationRequired = false
    let isDownloadRestricted = false
    let shared_employee_ids: number[] = []
    let shared_department_ids: number[] = []
    let notifyScope: AssetFormData['notifyScope'] = 'none'
    let notify_employee_ids: number[] = []
    let notify_department_ids: number[] = []
    let notes = ''

    if ('asset_id' in nextDraftAsset) {
        const detail = nextDraftAsset as AssetDetail
        const version = detail.current_version

        assetName = detail.asset_name
        assetDescription = detail.asset_description
        documentGroup = detail.document_group

        if (version) {
            documentType = version.document_type
            confidentialityLevel = version.confidentiality_level
            integrityLevel = version.integrity_level
            availabilityLevel = version.availability_level
            departmentId = version.department_id?.toString() || ''
            startDate = version.start_date?.split('T')[0] || ''
            endDate = version.end_date?.split('T')[0] || ''
            isReadConfirmationRequired =
                version.is_read_confirmation_required || false
            isDownloadRestricted = version.is_download_restricted || false
            notifyScope = version.notify_scope || 'none'
            notify_employee_ids = version.notify_employee_ids ?? []
            notify_department_ids = version.notify_department_ids ?? []
            notes = version.notes ?? ''
        }

        shared_employee_ids = detail.shared_employee_ids ?? []
        shared_department_ids = detail.shared_department_ids ?? []
    } else {
        const nested = nextDraftAsset as DraftAsset
        if (nested.asset) {
            assetName = nested.asset.asset_name
            assetDescription = nested.asset.asset_description
            documentGroup = nested.asset.document_group
        }
        if (nested.asset_version) {
            documentType = nested.asset_version.document_type
            confidentialityLevel = nested.asset_version.confidentiality_level
            integrityLevel = nested.asset_version.integrity_level
            availabilityLevel = nested.asset_version.availability_level
            departmentId = nested.asset_version.department_id?.toString() || ''
            startDate = nested.asset_version.start_date?.split('T')[0] || ''
            endDate = nested.asset_version.end_date?.split('T')[0] || ''
            isReadConfirmationRequired =
                nested.asset_version.is_read_confirmation_required || false
            isDownloadRestricted =
                nested.asset_version.is_download_restricted || false
            notifyScope = nested.asset_version.notify_scope || 'none'
            notify_employee_ids = nested.asset_version.notify_employee_ids ?? []
            notify_department_ids =
                nested.asset_version.notify_department_ids ?? []
            notes = nested.asset_version.notes ?? ''
        }

        shared_employee_ids =
            nested.shared_employee_ids ??
            nested.asset_version?.shared_employee_ids ??
            []
        shared_department_ids =
            nested.shared_department_ids ??
            nested.asset_version?.shared_department_ids ??
            []
    }

    const shareScope: 'public' | 'private' =
        shared_employee_ids.length > 0 || shared_department_ids.length > 0
            ? 'private'
            : 'public'

    return {
        assetName: assetName || '',
        assetDescription: assetDescription || '',
        documentGroup: documentGroup || '',
        documentType: documentType || '',
        confidentialityLevel: confidentialityLevel || '',
        integrityLevel: integrityLevel || '',
        availabilityLevel: availabilityLevel || '',
        departmentId,
        shareScope,
        shared_employee_ids,
        shared_department_ids,
        startDate: startDate || new Date().toISOString().split('T')[0],
        endDate: endDate || '',
        isReadConfirmationRequired,
        isDownloadRestricted,
        notes,
        notifyScope,
        notify_employee_ids,
        notify_department_ids,
    }
}
